import React, { useState, useEffect } from 'react';
import styles from '../../styles/scss/components/tips/relatedArticlesSection.module.scss';
import classNames from 'classnames';
import { supSub } from '../../services/util';
import { datoImage } from '../../services/datoImage';

const RelatedArticlesSection = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 992);
    };
    
    checkScreenSize();
    
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const handlePrevClick = () => {
    if (isMobile) {
      setCurrentIndex((prev) => (prev === 0 ? data.articles.length - 1 : prev - 1));
    } else if (isTablet) {
      // For tablet: 2 articles at a time
      setCurrentIndex((prev) => (prev === 0 ? data.articles.length - 2 : prev - 1));
    } else {
      // For desktop: 4 articles at a time
      setCurrentIndex((prev) => (prev === 0 ? data.articles.length - 4 : prev - 1));
    }
  };
  
  const handleNextClick = () => {
    if (isMobile) {
      setCurrentIndex((prev) => (prev === data.articles.length - 1 ? 0 : prev + 1));
    } else if (isTablet) {
      // For tablet: 2 articles at a time
      setCurrentIndex((prev) => (prev === data.articles.length - 2 ? 0 : prev + 1));
    } else {
      // For desktop: 4 articles at a time
      setCurrentIndex((prev) => (prev === data.articles.length - 4 ? 0 : prev + 1));
    }
  };
  
  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };
  
  // Get visible articles based on screen size
  const getVisibleArticles = () => {
    if (isMobile) {
      // Mobile: 1 article
      return [data.articles[currentIndex]].filter(Boolean);
    } else if (isTablet) {
      // Tablet: 2 articles
      return data.articles.slice(currentIndex, currentIndex + 2);
    } else {
      // Desktop: 4 articles
      return data.articles.slice(currentIndex, currentIndex + 4);
    }
  };
  
  const visibleArticles = getVisibleArticles();
  
  // Calculate progress percentage for progress bar
  const calculateProgress = () => {
    if (isMobile) {
      return (currentIndex / (data.articles.length - 1)) * 100;
    } else if (isTablet) {
      return (currentIndex / (data.articles.length - 2)) * 100;
    } else {
      return (currentIndex / (data.articles.length - 4)) * 100;
    }
  };
  
  return (
    <section className={styles.relatedArticlesSection}>
      <div className={styles.titleContainer}>
        <h2 className={styles.sectionTitle}>{data.title}</h2>
      </div>
      
      <div className={styles.articlesContainer}>
        {/* Articles */}
        <div className={styles.articlesList}>
          {visibleArticles.map((article) => (
            <a 
              key={article.id} 
              href={article.url} 
              className={styles.articleCard}
              aria-label={`Read article: ${article.title}`}
            >
              <img src={datoImage(article.image, { w: 700, h: 400, fit: 'crop' })} alt={article.title} className={styles.articleImage} />
              <div className={styles.categoryContainer}>
                <div className={styles.tagsContainer}>
                  {article.categories && article.categories.map((category, index) => (
                    <span key={index} className={styles.categoryTag} dangerouslySetInnerHTML={{__html: supSub(category)}}></span>
                  ))}
                </div>
                <h3 className={styles.articleTitle} dangerouslySetInnerHTML={{__html: supSub(article.title)}}></h3>
              </div>
            </a>
          ))}
        </div>
      </div>
      
      {/* Navigation Controls Container */}
      <div className={styles.controlsContainer}>
        {/* Desktop/Tablet Navigation Arrows - Hidden on Mobile */}
        {!isMobile && (
          <>
            <button className={styles.navArrow} onClick={handlePrevClick} aria-label="Previous articles">
              <svg width="12" height="24" viewBox="0 0 16 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.arrowLeft}>
                <path d="M15 1L2 15L15 29" stroke="#F5F5F5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <button className={styles.navArrow} onClick={handleNextClick} aria-label="Next articles">
              <svg width="12" height="24" viewBox="0 0 16 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.arrowRight}>
                <path d="M1 1L14 15L1 29" stroke="#F5F5F5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
        
        {/* Desktop/Tablet Progress Bar - Hidden on Mobile */}
        {!isMobile && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressIndicator} 
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {/* See All Blogs Button */}
        <div className={styles.seeAllContainer}>
          <a href="/blog" className={styles.seeAllButton}>SEE ALL BLOGS</a>
        </div>
      </div>
      
      {/* Mobile Dots Navigation - Only visible on Mobile */}
      {isMobile && (
        <div className={styles.dotsContainer}>
          {data.articles.map((_, index) => (
            <button 
              key={index} 
              className={classNames(styles.dot, { [styles.activeDot]: currentIndex === index })}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to article ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default RelatedArticlesSection;
